import { useEffect } from "react";

const SITE_ORIGIN = "https://elizareedwellness.com";

interface SeoOptions {
  /** Path part of the canonical URL, e.g. "/privacy". Defaults to current pathname. */
  path?: string;
  /** Document <title> */
  title?: string;
  /** Meta description */
  description?: string;
  /** If true, sets <meta name="robots" content="noindex, nofollow"> */
  noindex?: boolean;
}

const upsertMeta = (name: string, content: string) => {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
};

const upsertOg = (property: string, content: string) => {
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.content = content;
};

/**
 * Sets canonical URL, title, description, robots, and Open Graph / Twitter
 * tags so every route shares consistent SEO signals.
 * Canonical always points to https://elizareedwellness.com<path>.
 */
export const useSeo = ({ path, title, description, noindex }: SeoOptions = {}) => {
  useEffect(() => {
    const p = path ?? window.location.pathname;
    const href = `${SITE_ORIGIN}${p}`;

    // Canonical
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;

    // Title
    if (title) {
      document.title = title;
      upsertOg("og:title", title);
      upsertMeta("twitter:title", title);
    }

    // Description
    if (description) {
      upsertMeta("description", description);
      upsertOg("og:description", description);
      upsertMeta("twitter:description", description);
    }

    // Canonical URL on og:url too
    upsertOg("og:url", href);

    // Robots
    upsertMeta(
      "robots",
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    );
  }, [path, title, description, noindex]);
};

export default useSeo;
